import { useEffect, useState } from 'react';
import { ChevronsUpDown, Search, X } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { Skeleton } from '@/components/custom';
import { useRequestGetUsers } from '@/hooks/httpRequest';
import { useDebounce } from '@/hooks';
import { UserAccountStatusEnum } from '@/enums';
import type { ISearchUserData } from '@/lib/interface';

type TProps = {
	// the email of the member, empty for a public code
	value: string;
	onChange: (email: string) => void;
	disabled?: boolean;
};

// same search as the audience picker of the messages page, for one member
const PromoCodeUserField = ({ value, onChange, disabled }: TProps) => {
	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearchTerm = useDebounce(searchTerm, 500);

	const { users, isLoading } = useRequestGetUsers(
		{
			accountStatus: [UserAccountStatusEnum.ACTIVE],
			limit: 10,
			emailOrUsername: debouncedSearchTerm || undefined,
		},
		open
	);

	useEffect(() => {
		if (!open) setSearchTerm('');
	}, [open]);

	const pick = (user: ISearchUserData) => {
		onChange(user.email);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<div className="relative">
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="outline"
						role="combobox"
						aria-expanded={open}
						disabled={disabled}
						className="w-full justify-between px-3 font-normal text-xs"
					>
						<span className={value ? 'truncate pr-6' : 'text-muted-foreground'}>
							{value || 'Code public, ou cherche un membre'}
						</span>
						{!value && <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
					</Button>
				</PopoverTrigger>
				{value && !disabled && (
					<button
						type="button"
						aria-label="Retirer le membre"
						className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
						onClick={() => onChange('')}
					>
						<X className="h-4 w-4" />
					</button>
				)}
			</div>
			<PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
				<div className="flex items-center border-b px-3">
					<Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
					<input
						className="flex h-10 w-full bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground"
						placeholder="Rechercher par nom ou email..."
						value={searchTerm}
						onChange={event => setSearchTerm(event.target.value)}
					/>
				</div>
				<div className="max-h-[200px] overflow-y-auto p-1">
					{isLoading &&
						Array.from({ length: 3 }, (_, index) => (
							<div key={`user-skeleton-${index}`} className="space-y-1.5 px-2 py-2">
								<Skeleton className="h-3.5 w-32" />
								<Skeleton className="h-3 w-44" />
							</div>
						))}
					{!isLoading && users.length === 0 && (
						<div className="py-6 text-center text-sm text-muted-foreground">Aucun utilisateur trouvé.</div>
					)}
					{users.map((user: ISearchUserData) => (
						<button
							type="button"
							key={user._id}
							onClick={() => pick(user)}
							className="flex w-full cursor-pointer flex-col rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
						>
							<span className="truncate font-medium">{user.username}</span>
							<span className="truncate text-xs text-muted-foreground">{user.email}</span>
						</button>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default PromoCodeUserField;
